/**
 * PATH: packages/api/src/view-preferences/contract.ts
 * FILE_ID: API-VIEWPREF-CONTRACT-001
 * PURPOSE: Define the generic identity, cache, transport, revision, and size contract.
 * ROLE IN ARCHITECTURE: Framework-agnostic boundary for durable UI preferences.
 * MAIN EXPORTS: ViewPreferenceTransport, ViewPreferenceCache,
 *   viewPreferenceCacheKey, normalizeViewPreferenceEtag.
 * NON-RESPONSIBILITIES: Does not choose auth, HTTP, browser storage, or domain schemas.
 * NOTES FOR FUTURE AI: Full-blob writes require revision and schema metadata.
 *   Prefer the body validator; strip W/ before If-Match.
 */

import type { BackendSchemas } from '../contracts'

export const VIEW_PREFERENCE_ENDPOINT = '/api/settings/view-preferences/'
export const VIEW_PREFERENCE_MAX_STATE_BYTES = 64 * 1024
export const VIEW_PREFERENCE_SCHEMA_HEADER =
  'X-View-Preference-Schema-Version'
export const VIEW_PREFERENCE_ETAG_HEADER = 'ETag'

/**
 * Return the comparable validator token for one ETag or If-Match value.
 *
 * Inputs: a header, body etag, or empty. Output: the same token with an
 * optional RFC 9110 weak `W/` prefix stripped, or null when absent.
 * Side effects: none. Failure modes: blank input becomes null.
 */
export function normalizeViewPreferenceEtag(
  value: string | null | undefined,
): string | null {
  if (value == null) return null
  let token = String(value).trim()
  if (!token) return null
  if (/^w\//i.test(token)) token = token.slice(2).trim()
  return token || null
}

export interface ViewPreferenceIdentity {
  /** Stable tenant namespace. It is not a display name. */
  tenant: string
  /** Stable authenticated user id. Anonymous preference writes are invalid. */
  userId: string | number
}

export interface ViewPreferenceEnvelope {
  state: Record<string, unknown>
  etag: string
  schemaVersion: number
}

/** Exact snake-case single-scope body generated from the Django endpoint. */
export type ViewPreferenceWireEnvelope =
  BackendSchemas['ViewPreferenceEnvelope']

export type ViewPreferenceConflictStatus = 409 | 412 | 428

/** Exact generated rejection body when the backend exposes recovery state. */
export type ViewPreferenceConflictWireEnvelope =
  BackendSchemas['ViewPreferenceConflict']

export interface ViewPreferenceReadRequest {
  scopeKey: string
  actionId: string
}

export interface ViewPreferencePatchRequest {
  scopeKey: string
  state: Record<string, unknown>
  ifMatch: string
  schemaVersion: number
  actionId: string
}

export interface ViewPreferenceDeleteRequest {
  scopeKey: string
  ifMatch: string
  schemaVersion: number
  actionId: string
}

export interface ViewPreferenceTransport {
  /** Read one opaque scope and return its current server revision. */
  get(request: ViewPreferenceReadRequest): Promise<ViewPreferenceEnvelope>
  /** Replace one complete scope when the supplied revision is current. */
  patch(request: ViewPreferencePatchRequest): Promise<ViewPreferenceEnvelope>
  /** Delete one scope when the supplied revision is current. */
  delete(request: ViewPreferenceDeleteRequest): Promise<ViewPreferenceEnvelope>
}

export interface ViewPreferenceCache {
  /** Read one untrusted cached value. */
  read(key: string): unknown
  /** Replace one cached value synchronously. */
  write(key: string, value: unknown): void
  /** Delete one cached value. */
  remove(key: string): void
  /** Subscribe to external writes for this exact identity/scope key. */
  subscribe?: (key: string, listener: (value: unknown) => void) => () => void
}

function requiredSegment(name: string, value: string | number): string {
  const normalized = String(value).trim()
  if (!normalized) {
    throw new Error(`viewPreferenceCacheKey: ${name} must be non-empty`)
  }
  return encodeURIComponent(normalized)
}

/**
 * Build the cache/debounce key for exactly one tenant, user, and scope.
 *
 * Inputs: authenticated identity and a non-empty opaque scope.
 * Output: stable encoded key safe for browser storage and in-memory maps.
 * Side effects: none.
 * Failure: throws when any identity segment or scope is blank.
 */
export function viewPreferenceCacheKey(
  identity: ViewPreferenceIdentity,
  scopeKey: string,
): string {
  return [
    'viewpref',
    requiredSegment('tenant', identity.tenant),
    requiredSegment('userId', identity.userId),
    requiredSegment('scopeKey', scopeKey),
  ].join('.')
}
